import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    Typography,
    Alert,
    IconButton,
    InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import apiService from '@services/common';

const ChangePasswordModal = ({ open, onClose, user, onSuccess }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // State cho nút hiển thị mật khẩu
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Mỗi lần mở dialog / đổi user thì reset form
    useEffect(() => {
        if (open) {
            setNewPassword('');
            setConfirmPassword('');
            setError(null);
            setSuccess(null);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        }
    }, [open, user]);

    if (!user) return null; // chưa có user thì không render

    // Hàm xử lý hiển thị/ẩn mật khẩu
    const handleClickShowNewPassword = () => {
        setShowNewPassword(!showNewPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!newPassword) {
            setError('Vui lòng nhập mật khẩu mới');
            return;
        }

        if (newPassword.length < 6) {
            setError('Mật khẩu mới phải ít nhất 6 ký tự');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Xác nhận mật khẩu không khớp');
            return;
        }

        try {
            setSubmitting(true);

            await apiService.post(`change-password`, {
                user_id: user.id,
                new_password: newPassword,
                new_password_confirmation: confirmPassword,
            });

            setSuccess('Đổi mật khẩu thành công');
            if (typeof onSuccess === 'function') {
                onSuccess(user);
            }

            // Đợi chút cho user thấy message rồi đóng
            setTimeout(() => {
                onClose();
            }, 500);
        } catch (err) {
            console.error(err);
            // Nếu backend trả message:
            const message =
                err?.response?.data?.message ||
                'Đã có lỗi xảy ra khi đổi mật khẩu';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Đổi mật khẩu</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Đổi mật khẩu cho người dùng:{' '}
                    <strong>{user.name}</strong> ({user.email})
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        {/* Trường mật khẩu mới với nút hiển thị */}
                        <TextField
                            label="Mật khẩu mới"
                            type={showNewPassword ? "text" : "password"}
                            fullWidth
                            size='small'
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle new password visibility"
                                            onClick={handleClickShowNewPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            size="small"
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Trường xác nhận mật khẩu với nút hiển thị */}
                        <TextField
                            label="Xác nhận mật khẩu mới"
                            type={showConfirmPassword ? "text" : "password"}
                            fullWidth
                            size='small'
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle confirm password visibility"
                                            onClick={handleClickShowConfirmPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            size="small"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Stack>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Hủy
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Đang lưu...' : 'Đổi mật khẩu'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordModal;